/** Parse Jackrabbit ISO duration (e.g. P05Y00M, P01Y06M) to total months. */
export const parseJackrabbitAgeDurationToMonths = (duration) => {
  if (!duration || typeof duration !== "string") return null;

  const match = duration.match(/^P(?:(\d+)Y)?(?:(\d+)M)?$/i);
  if (!match) return null;

  const years = Number(match[1] || 0);
  const months = Number(match[2] || 0);
  return years * 12 + months;
};

const formatAgeMonths = (totalMonths) => {
  if (totalMonths === null || totalMonths < 0) return null;

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  if (years === 0) {
    return months === 1 ? "1 mo" : `${months} mo`;
  }
  if (months === 0) {
    return years === 1 ? "1 yr" : `${years} yrs`;
  }
  if (years === 1) {
    return `1 yr ${months} mo`;
  }
  return `${years} yrs ${months} mo`;
};

/** Human-readable age range for schedule tables, e.g. "Ages 5–6". */
export const formatClassAgeRange = (minAge, maxAge) => {
  const minMonths = parseJackrabbitAgeDurationToMonths(minAge);
  const maxMonths = parseJackrabbitAgeDurationToMonths(maxAge);

  if (minMonths === null && maxMonths === null) return null;
  if (minMonths !== null && maxMonths !== null) {
    const minYears = Math.floor(minMonths / 12);
    const maxYears = Math.floor(maxMonths / 12);

    if (minMonths % 12 === 0 && maxMonths % 12 >= 11 && maxYears > minYears) {
      return minYears === maxYears ? `Age ${minYears}` : `Ages ${minYears}–${maxYears}`;
    }
    if (minYears === maxYears && minMonths % 12 === 0 && maxMonths % 12 === 0) {
      return minYears === 1 ? "Age 1" : `Ages ${minYears}`;
    }
  }

  const minLabel = formatAgeMonths(minMonths);
  const maxLabel = formatAgeMonths(maxMonths);

  if (minLabel && maxLabel) {
    return `Ages ${minLabel} – ${maxLabel}`;
  }

  return minLabel ? `Ages ${minLabel}+` : maxLabel ? `Up to ${maxLabel}` : null;
};

/** @returns {boolean} */
export const childAgeMatchesClass = (childYears, childMonths, minAge, maxAge) => {
  if (childYears === null || childYears === undefined || childYears === "") {
    return true;
  }

  const childTotalMonths = Number(childYears) * 12 + Number(childMonths || 0);
  if (Number.isNaN(childTotalMonths)) return true;

  const minMonths = parseJackrabbitAgeDurationToMonths(minAge);
  const maxMonths = parseJackrabbitAgeDurationToMonths(maxAge);

  if (minMonths === null && maxMonths === null) return true;
  if (minMonths !== null && childTotalMonths < minMonths) return false;
  if (maxMonths !== null && childTotalMonths > maxMonths) return false;

  return true;
};

export const CHILD_AGE_YEAR_OPTIONS = [
  { value: "", label: "Any age" },
  ...Array.from({ length: 18 }, (_, year) => ({
    value: String(year),
    label: year === 1 ? "1 year" : `${year} years`,
  })),
];

export const CHILD_AGE_MONTH_OPTIONS = Array.from({ length: 12 }, (_, month) => ({
  value: String(month),
  label: month === 1 ? "1 month" : `${month} months`,
}));
